import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { Popup } from "@/Common/Components/Modals/Popup";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";
import { relativeStore } from "../Stores/RelativeStore";

interface IRelativeMemoPopupProps {
    person: Person;
    onClose: () => void;
}

class RelativeMemoPopupState {
    public memoText: string = "";
    public memoDate: Date;
    public memoUserId: Guid = null;
    public isInEditMode: boolean = true;
}

class RelativeMemoPopup extends BaseReactComponent<IRelativeMemoPopupProps, RelativeMemoPopupState> {
    public constructor(props: IRelativeMemoPopupProps) {
        super(props);
        const newState = new RelativeMemoPopupState();
        newState.memoDate = this.props.person.memoLastModifiedDate;
        newState.memoText = this.props.person.memoNote ?? "";
        newState.memoUserId = this.props.person.lastModifiedMemoEmployeeId;
        newState.isInEditMode = !this.props.person.memoNote;
        this.state = newState;
    }

    public render(): JSX.Element {
        let employeeName = "";

        if (this.state !== null && this.state.memoUserId !== null) {
            const employee = cacheStore.GetEmployeeByContactFacetId(this.state.memoUserId);
            if (employee) {
                employeeName = `${employee.LastName}, ${employee.FirstName}`;
            }
        }

        return (
            <Popup
                Id="popupmenu-relative"
                title={cacheStore.GetString("MemoLabel")}
                draggable
                manageOverlay
                centeredHorizontally
                width={550}
                onCloseDelegate={() => this.props.onClose()}
            >
                <div className="p-body">
                    <div className="row">
                        <div className="col-lg-12">
                            {!this.state.isInEditMode && (
                                <div className="row">
                                    <div className="col-sm-4">
                                        {employeeName} <br />
                                        {(this.state.memoDate) ? moment(this.state.memoDate).format("YYYY-MM-DD HH:mm:SS") : ""}
                                    </div>
                                    <div className="col-sm-8">
                                        <span> {JSXHelper.BreakStringIntoParagraphs(this.state.memoText)} </span>
                                    </div>
                                </div>
                            )}
                            {this.state.isInEditMode && (
                                <div className="row">
                                    <div className="col-sm-12">
                                        <TextAreaAutoSize
                                            id="memo_text"
                                            ref="memo_text"
                                            value={this.state.memoText}
                                            onTextChangeCallback={value => this.MemoTextChanged(value)}
                                            minHeight="7em"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="p-footer flex-display flex-end">
                    {this.state.isInEditMode && (
                        <ButtonGroup>
                            <ButtonControl
                                variant="neutral"
                                onClick={() => this.props.onClose()}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                onClick={() => this.SaveClick()}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </ButtonGroup>
                    )}
                    {!this.state.isInEditMode && (
                        <ButtonGroup>
                            <ButtonControl
                                variant="neutral"
                                onClick={() => this.props.onClose()}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                onClick={() => this.enterEditMode()}
                                label={cacheStore.GetString("ModifyLabel")}
                            />
                            <ButtonControl
                                variant="error"
                                onClick={() => this.DeleteClick()}
                                label={cacheStore.GetString("DeleteLabel")}
                            />
                        </ButtonGroup>
                    )}
                </div>
            </Popup>
        );
    }

    private MemoTextChanged(value: string): void {
        this.setState({ memoText: value });
    }

    private DeleteClick(): void {
        relativeStore.actions.saveMemo(this.state.memoText, this.props.person.ContactId, true);
        this.props.onClose();
    }

    private enterEditMode(): void {
        this.setState({ isInEditMode: true });
    }

    private SaveClick(): void {
        this.props.onClose();
        relativeStore.actions.saveMemo(this.state.memoText, this.props.person.ContactId);
    }
}

export { RelativeMemoPopup, RelativeMemoPopupState };
